/**
 * Pure helpers for folding a LIVE turn's streamed state into a message list.
 *
 * Every consumer that renders an in-flight turn from stream progress — the
 * interrupted-banner resume and the read-only reattach — applies these same
 * rules, so "arrived late" and "watched live" cannot drift apart. (The send
 * pipeline still folds through its own streaming-message sync; collapsing all
 * three onto one reducer is the planned conversation-feed refactor.)
 */
import { ChatbotMessage, MessageType } from './types';

export type ToolMarker = {
  position: number;
  tool: string;
  runId?: string;
  message?: string;
  success?: boolean | null;
};

export type TurnReplyUpsert =
  | { action: 'update'; messages: ChatbotMessage[] }
  | { action: 'append' }
  | { action: 'skip' };

/**
 * Upsert streamed reply content into the CURRENT turn's assistant bubble.
 *
 * The current turn's bubble is an assistant message sitting AFTER the last
 * user message; when the last message is the user's (no reply bubble yet) the
 * caller appends one. Shorter content never overwrites a longer bubble —
 * progress payloads can repeat around a reconnect, and a replay must never
 * rewind visible text — except when `rewind` says the answer so far was taken
 * back (`reply_reset`), which rewrites the bubble there is and adds none.
 */
export function upsertTurnReply(
  messages: ChatbotMessage[],
  content: string,
  toolMarkers?: ToolMarker[],
  turnId?: string,
  rewind = false,
): TurnReplyUpsert {
  // Continuations and Executive activations need not have a new user bubble.
  // Their durable identity is the boundary; never reuse an earlier reply.
  if (turnId) {
    const reverseIndex = [...messages].reverse().findIndex(
      message => message.type === MessageType.ASSISTANT && message.turnId === turnId && !message.hidden,
    );
    if (reverseIndex === -1) return { action: rewind ? 'skip' : 'append' };
    const targetIndex = messages.length - 1 - reverseIndex;
    const target = messages[targetIndex];
    if (!rewind && typeof target.content === 'string' && target.content.length > content.length) {
      return { action: 'skip' };
    }
    const updated = [...messages];
    updated[targetIndex] = { ...target, content, toolMarkers: toolMarkers || target.toolMarkers };
    return { action: 'update', messages: updated };
  }
  // Indices are into the REVERSED list: smaller = closer to the end.
  const lastUserIndex = [...messages].reverse().findIndex(m => m.type === MessageType.USER);
  const lastAssistantIndex = [...messages].reverse().findIndex(m => m.type === MessageType.ASSISTANT);
  if (lastAssistantIndex === -1 || (lastUserIndex !== -1 && lastAssistantIndex >= lastUserIndex)) {
    // A rewind takes back a bubble's answer; with no bubble there is none to take back.
    return { action: rewind ? 'skip' : 'append' };
  }
  const targetIndex = messages.length - 1 - lastAssistantIndex;
  const existingContent = messages[targetIndex]?.content;
  if (!rewind && typeof existingContent === 'string' && existingContent.length > content.length) {
    return { action: 'skip' };
  }
  const updated = [...messages];
  updated[targetIndex] = {
    ...updated[targetIndex],
    content,
    toolMarkers: toolMarkers || updated[targetIndex].toolMarkers,
  };
  return { action: 'update', messages: updated };
}

/**
 * Merge an S3 conversation snapshot with the live turn's streamed tail.
 *
 * Keep the snapshot's history and re-append the live assistant tail until its
 * durable identity is saved. For older unscoped turns, retain the ordinary
 * chat rule: a saved reply after the last person message replaces the tail.
 */
// The person's last message. A notice the harness appended (a returned run) is not a turn.
const lastPersonIndex = (messages: ChatbotMessage[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].type === MessageType.USER && !messages[i].harness) return i;
  }
  return -1;
};

export function mergeSnapshotWithLiveTail(
  current: ChatbotMessage[],
  snapshot: ChatbotMessage[],
): ChatbotMessage[] {
  const lastUserIdxCur = lastPersonIndex(current);
  const lastAssistant = [...current].reverse().find(
    message => message.type === MessageType.ASSISTANT && !message.hidden,
  );
  if (lastAssistant?.turnId && current.indexOf(lastAssistant) > lastUserIdxCur) {
    // A snapshot can already contain the original reply while still missing
    // the later activation. Keep precisely that live turn until saved. A
    // saved reply must carry the same durable ID; text is not identity.
    const saved = snapshot.some(message =>
      message.type === MessageType.ASSISTANT && !message.hidden && message.turnId === lastAssistant.turnId,
    );
    return saved ? snapshot : [...snapshot, lastAssistant];
  }
  const liveTail = current
    .slice(lastUserIdxCur + 1)
    .filter(m => m.type === MessageType.ASSISTANT && !m.hidden);
  if (liveTail.length === 0) return snapshot;
  const lastUserIdxSnap = lastPersonIndex(snapshot);
  const snapshotHasReply = snapshot
    .slice(lastUserIdxSnap + 1)
    .some(m => m.type === MessageType.ASSISTANT && !m.hidden);
  return snapshotHasReply ? snapshot : [...snapshot, ...liveTail];
}

/** Explicit turn boundaries prevent reload folding an old reply into a continuation. */
export function crossesTurnBoundary(left: ChatbotMessage, right: ChatbotMessage): boolean {
  return Boolean((left.turnId || right.turnId) && left.turnId !== right.turnId);
}

/** A module return carries no user message to resend, even before its first token. */
export function continuationRecoveryContext(messages: ChatbotMessage[], turnId?: string) {
  if (!turnId?.startsWith('continuation_')) return null;
  const reply = [...messages].reverse().find(message =>
    message.type === MessageType.ASSISTANT && message.turnId === turnId && !message.hidden,
  );
  return { turnId, originalMessage: '', partialResponse: typeof reply?.content === 'string' ? reply.content : '' };
}

/**
 * What a turn's reader has of the streamed text: everything (`fullText`, the
 * working text the pill reads), the answer body (`conclusionText`, the
 * `conclusion_render` tokens) and where the current answer began in the whole.
 */
export type StreamedTurnText = { fullText: string; conclusionText: string; answerStart: number | null };

/**
 * `reply_reset`: the model call that was writing the answer is being tried
 * again, or handed to another model, and will write the answer anew. Take back
 * only that answer; the working text before it and the steps stay, unlike
 * `response_reset`, which starts the whole turn over.
 */
export function takeBackAnswer(text: StreamedTurnText): StreamedTurnText {
  return {
    fullText: text.answerStart === null ? text.fullText : text.fullText.slice(0, text.answerStart),
    conclusionText: '',
    answerStart: null,
  };
}

/** The `status` events whose message is the Working pill's activity line. */
const ACTIVITY_NODES: ReadonlySet<string> = new Set(['turn_handoff', 'model_stall']);

/**
 * What a `status` event does to the pill's activity line: a line to show, `null`
 * to clear it, or `undefined` to leave it. The harness handing the turn to a
 * fresh worker (`turn_handoff`) and the watchdog on a quiet model
 * (`model_stall`: waiting, retrying, switching) say what is happening; a model
 * that is back (`resumed`) clears the line, which returns to the step at hand.
 */
export function statusActivityNote(event: { node?: unknown; phase?: unknown; message?: unknown }): string | null | undefined {
  if (typeof event.node !== 'string' || !ACTIVITY_NODES.has(event.node)) return undefined;
  if (event.phase === 'resumed') return null;
  return typeof event.message === 'string' && event.message.trim() ? event.message : undefined;
}
